import { Link } from '@material-ui/core';
import { Add, Email } from '@material-ui/icons';
import { Inline, Stack } from '@superdispatch/ui';
import {
  Box,
  DescriptionItem,
  MultilineText,
  TextBox,
} from '@superdispatch/ui-lab';
import { parseURITemplate } from '@superdispatch/uri';
import { MouseEvent, ReactNode, useMemo } from 'react';
import { MobileAppBridge } from 'shared/data/MobileAppBridge';
import { ShipperProfileLink } from 'shared/modules/shipper/ShipperProfileLink';
import { WebViewLinkedText } from 'shared/routing/WebViewLinks';
import { joinStrings } from 'shared/utils/StringUtils';
import {
  PostingLoadDTO,
  PostingShipperDTO,
} from '../../data/dto/PostingLoadDTO';

function formatShipperAddress({
  address,
  city,
  state,
  zip,
}: PostingShipperDTO) {
  return joinStrings(
    ', ',
    joinStrings(' ', address, city),
    joinStrings(' ', state, zip),
  );
}

interface PostingShipperRequirementsProps {
  load: PostingLoadDTO;
  carrierInsuranceRequirements?: ReactNode;
}

export function PostingShipperRequirements({
  load,
  carrierInsuranceRequirements,
}: PostingShipperRequirementsProps) {
  const shipperProfileAddress = useMemo(
    () => load.shipper && formatShipperAddress(load.shipper),
    [load.shipper],
  );

  return (
    <Stack>
      {load.carrier_access.is_certificate_required &&
        !!load.shipper?.carrier_certificate_of_insurance && (
          <Box
            padding="xsmall"
            backgroundColor="Silver200"
            borderRadius="small"
          >
            <Stack>
              <Stack space="xxsmall">
                <TextBox variant="body-semibold">
                  {load.shipper.carrier_certificate_of_insurance ===
                  'CERTIFICATE_HOLDER'
                    ? 'List Certificate Holder'
                    : 'List Additionally Insured'}{' '}
                  as:
                </TextBox>

                <span>{load.shipper.name}</span>

                {!!shipperProfileAddress && (
                  <span>{shipperProfileAddress}</span>
                )}
              </Stack>

              {!!load.shipper.contact_email && (
                <Stack space="xxsmall">
                  <TextBox color="secondary">Send info to:</TextBox>
                  <DescriptionItem
                    icon={<Email />}
                    aria-label="email for the insurance info"
                  >
                    {load.shipper.contact_email}
                  </DescriptionItem>
                </Stack>
              )}
            </Stack>
          </Box>
        )}

      {carrierInsuranceRequirements && (
        <Box padding="xsmall" backgroundColor="Silver200" borderRadius="small">
          {carrierInsuranceRequirements}
        </Box>
      )}

      {load.carrier_access.is_ach_payment_required && (
        <Box padding="xsmall" borderRadius="small" backgroundColor="Silver200">
          <Link
            color="primary"
            variant="body1"
            // eslint-disable-next-line no-restricted-syntax
            href={parseURITemplate(
              '/tms/profile/ach_payment{?utm_medium,back_url}',
              {
                utm_medium: 'slb_load_details',
                back_url: window.location.href,
              },
            )}
            onClick={(event: MouseEvent) => {
              if (MobileAppBridge.isInjected()) {
                event.preventDefault();
                MobileAppBridge.openACHPaymentInformation();
              }
            }}
          >
            <Inline verticalAlign="center">
              <Add fontSize="small" color="inherit" />

              <span>Add ACH Payment Information</span>
            </Inline>
          </Link>
        </Box>
      )}

      {!!load.shipper?.carrier_requirements && (
        <Stack>
          <Box maxHeight={['68px', '60px']} overflow="hidden">
            <MultilineText overflowWrap="break-word">
              <WebViewLinkedText>
                {load.shipper.carrier_requirements}
              </WebViewLinkedText>
            </MultilineText>
          </Box>

          <ShipperProfileLink
            color="primary"
            guid={load.shipper.guid}
            medium="Shipper Requirements"
            onClick={(event: MouseEvent) => {
              event.stopPropagation();
            }}
          >
            Read More
          </ShipperProfileLink>
        </Stack>
      )}
    </Stack>
  );
}
